import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  FaFileImage,
} from "react-icons/fa";
import { MdOutlineFileDownload, MdOutlineFeedback } from "react-icons/md";
import { RiEditLine, RiShareForwardLine } from "react-icons/ri";
import PropTypes from "prop-types";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PosterViewDialog = ({
  open,
  onClose,
  posterUrl = "",
  posterId,
  onStartRegenerate,
  onDownload,
}) => {
  const [alertInfo, setAlertInfo] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState("");
  const [feedbackConsent, setFeedbackConsent] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [previousFeedbacks, setPreviousFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const token = localStorage.getItem("authToken");

  const handleComingSoon = () => {
    setAlertInfo({ severity: "info", message: "Coming soon" });
  };

  const handleCloseAlert = () => {
    setAlertInfo(null);
  };

  const handleShare = async () => {
    if (!posterId) {
      setAlertInfo({ severity: "error", message: "Poster not available for sharing" });
      return;
    }

    setSharingInProgress(true);
    setShareDialogOpen(true);
    setShareUrl("");

    try {
      // First, try GET to check if share link already exists
      const getResponse = await fetch(
        `${BASE_URL}/poster/${posterId}/share`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (getResponse.ok) {
        const getData = await getResponse.json();
        if (getData.share_url) {
          setShareUrl(getData.share_url);
          setSharingInProgress(false);
          return;
        }
      }

      // If no existing link, create one with POST
      const postResponse = await fetch(
        `${BASE_URL}/poster/${posterId}/share`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ posterId: posterId }),
        }
      );

      if (postResponse.ok) {
        const postData = await postResponse.json();
        setShareUrl(postData.share_url);
      } else {
        console.error("[PosterViewDialog] Share failed:", postResponse.status);
        setAlertInfo({ severity: "error", message: "Failed to create share link" });
        setShareDialogOpen(false);
      }
    } catch (error) {
      console.error("[PosterViewDialog] Error creating share link:", error);
      setAlertInfo({ severity: "error", message: "Error creating share link" });
      setShareDialogOpen(false);
    } finally {
      setSharingInProgress(false);
    }
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setAlertInfo({ severity: "success", message: "Link copied to clipboard!" });
    } catch (error) {
      console.error("[PosterViewDialog] Error copying to clipboard:", error);
      setAlertInfo({ severity: "error", message: "Failed to copy link" });
    }
  };

  const handleOpenFeedback = () => {
    setFeedbackRating(0);
    setFeedbackComments("");
    setFeedbackConsent(false);
    setFeedbackDialogOpen(true);
    fetchPreviousFeedbacks();
  };

  const fetchPreviousFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const url = `${BASE_URL}/project_feedbacks?id=${posterId}&type=poster&version=1`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setPreviousFeedbacks(data.feedbacks || []);
      } else {
        console.error("Failed to fetch previous feedbacks:", response.status);
        setPreviousFeedbacks([]);
      }
    } catch (error) {
      console.error("Error fetching previous feedbacks:", error);
      setPreviousFeedbacks([]);
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (feedbackRating === 0) {
      setAlertInfo({ severity: "warning", message: "Please select a rating" });
      return;
    }

    setSubmittingFeedback(true);
    try {
      const formData = new FormData();
      formData.append("stars", feedbackRating);
      formData.append("comments", feedbackComments);
      formData.append("id", posterId);
      formData.append("type", "poster");
      formData.append("version", 1);

      const response = await fetch(`${BASE_URL}/submit_feedback`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        setAlertInfo({ severity: "success", message: "Feedback submitted successfully!" });
        setFeedbackDialogOpen(false);
        setFeedbackRating(0);
        setFeedbackComments("");
        setFeedbackConsent(false);
      } else {
        console.error("[PosterViewDialog] Feedback submission failed:", response.status);
        setAlertInfo({ severity: "error", message: "Failed to submit feedback" });
      }
    } catch (error) {
      console.error("[PosterViewDialog] Error submitting feedback:", error);
      setAlertInfo({ severity: "error", message: "Error submitting feedback" });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ratingOptions = [
    { value: 1, label: "Awful", emoji: "😣" },
    { value: 2, label: "Poor", emoji: "😞" },
    { value: 3, label: "Neutral", emoji: "😐" },
    { value: 4, label: "Good", emoji: "🤩" },
    { value: 5, label: "Excellent", emoji: "😍" },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: "900px",
            maxWidth: "900px",
            borderRadius: "8px",
            overflow: "visible",
            fontFamily: "'Inter', sans-serif",
          },
        }}
        sx={{
          "& *": { fontFamily: "'Inter', sans-serif !important" },
        }}
      >
        <IconButton
          style={{ position: "absolute", top: 8, right: 8, color: "#838383" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>

        <DialogTitle style={{ padding: "16px", borderBottom: "1px solid #eee" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: "16px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FaFileImage style={{ color: "#1E4DE3", fontSize: "20px" }} />
            </div>



            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: "8px",
                marginRight: "48px",
              }}
            >
              <Tooltip title="Coming soon">
                <Button
                  variant="contained"
                  startIcon={<RiEditLine style={{ color: "#000" }} />}
                  onClick={handleComingSoon}
                  style={{
                    backgroundColor: "#d3d3d3",
                    color: "#000",
                    textTransform: "none",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Edit
                </Button>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<MdOutlineFileDownload style={{ color: "#1E4DE3" }} />}
                onClick={onDownload}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Download
              </Button>

              <Tooltip title="Coming soon">
                <Button
                  variant="contained"
                  startIcon={<RiShareForwardLine style={{ color: "#000" }} />}
                  onClick={handleComingSoon}
                  style={{
                    backgroundColor: "#d3d3d3",
                    color: "#000",
                    textTransform: "none",
                    boxShadow: "none",
                    cursor: "not-allowed",
                  }}
                >
                  Share
                </Button>
              </Tooltip>

              <Button
                variant="outlined"
                startIcon={<MdOutlineFeedback style={{ color: "#1E4DE3" }} />}
                onClick={handleOpenFeedback}
                sx={{
                  borderColor: "#d3d3d3",
                  color: "#1E4DE3",
                  textTransform: "none",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderColor: "#1E4DE3",
                    boxShadow: "0 2px 8px rgba(30, 77, 227, 0.2)",
                    backgroundColor: "#e8f0fe",
                  },
                }}
              >
                Feedback
              </Button>
            </div>
          </div>
        </DialogTitle>

        <DialogContent style={{ padding: "16px" }}>
          <div
            style={{
              position: "relative",
              width: "100%",
              paddingTop: "56.25%", // 16:9 ratio
              backgroundColor: "#000",
              marginBottom: "16px",
            }}
          >
            {posterUrl ? (
              // ✅ Render iframe at 2× dimensions then scale down via CSS transform.
              // This forces Office Online to rasterize at double resolution, eliminating
              // the blurriness caused by its default low-DPI rendering.
              (() => {
                const SCALE = 2;
                return (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      key={posterUrl}
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        posterUrl
                      )}`}
                      style={{
                        border: "none",
                        // Render at 2× logical size
                        width: `${100 * SCALE}%`,
                        height: `${100 * SCALE}%`,
                        // Scale back down to fit — origin top-left keeps it anchored correctly
                        transform: `scale(${1 / SCALE})`,
                        transformOrigin: "top left",
                      }}
                      title="Poster Viewer"
                    />
                  </div>
                );
              })()
            ) : (
              <p
                style={{
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                No poster available
              </p>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<FaFileImage style={{ color: "#fff" }} />}
              onClick={handleComingSoon}
              style={{
                backgroundColor: "#1E4DE3",
                color: "#fff",
                textTransform: "none",
                boxShadow: "none",
                cursor: "pointer",
              }}
            >
              Regenerate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Snackbar for alerts - displayed outside the dialog */}
      <Snackbar
        open={!!alertInfo}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertInfo?.severity || "info"}
          sx={{ width: '100%' }}
        >
          {alertInfo?.message}
        </Alert>
      </Snackbar>

      {/* Share Link Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "8px",
              minWidth: 400,
              backgroundColor: "#2d2d2d",
              color: "#fff",
            },
          },
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          color: "#fff"
        }}>
          <span style={{ fontSize: "16px" }}>Link to 'Pubvision Poster' copied</span>
          <IconButton
            onClick={() => setShareDialogOpen(false)}
            sx={{ color: "#fff", padding: "4px" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "0 16px 16px 16px" }}>
          {sharingInProgress ? (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
            }}>
              <CircularProgress size={24} sx={{ color: "#fff" }} />
              <span style={{ marginLeft: "12px", color: "#fff" }}>Loading share link...</span>
            </div>
          ) : (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#3d3d3d",
              borderRadius: "4px",
              padding: "8px 12px",
            }}>
              <input
                type="text"
                value={shareUrl}
                readOnly
                style={{
                  flex: 1,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  fontSize: "14px",
                  outline: "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              />
              <Button
                variant="contained"
                onClick={handleCopyShareUrl}
                startIcon={<ContentCopyIcon />}
                sx={{
                  backgroundColor: "#4a4a4a",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#5a5a5a",
                  },
                }}
              >
                Copy
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              padding: "16px",
              minWidth: 400,
              maxWidth: 450,
              maxHeight: "90vh",
            },
          },
        }}
      >
        <DialogTitle sx={{
          padding: "0 0 16px 0",
          fontWeight: 600,
          fontSize: "18px",
        }}>
          Help us improve our product for you
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflowY: "auto" }}>
          {loadingFeedbacks ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              {/* Previous Reviews Section */}
              {previousFeedbacks.length > 0 && (
                <Box sx={{ marginBottom: "24px" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                  >
                    Previous Reviews
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      padding: "8px",
                    }}
                  >
                    {previousFeedbacks.map((feedback, index) => {
                      const matchedOption = ratingOptions.find(
                        (opt) => opt.value === feedback.stars
                      ) || { emoji: "❓", label: "Unknown" };
                      return (
                        <Box
                          key={index}
                          sx={{
                            padding: "12px",
                            marginBottom: "8px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "6px",
                            "&:last-child": { marginBottom: 0 },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <span style={{ fontSize: "24px", marginRight: "8px" }}>
                              {matchedOption.emoji}
                            </span>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#333" }}
                            >
                              {matchedOption.label}
                            </Typography>
                          </Box>
                          {feedback.comments && (
                            <Typography
                              variant="body2"
                              sx={{ color: "#555", marginBottom: "4px" }}
                            >
                              {feedback.comments}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            {new Date(feedback.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Add New Review Section */}
              {previousFeedbacks.length > 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}
                >
                  Add New Review
                </Typography>
              )}

              <p style={{ margin: "0 0 16px 0", color: "#666", fontSize: "14px" }}>
                {previousFeedbacks.length > 0
                  ? "Share your latest thoughts with us:"
                  : "How would you like to describe your experience with the app today?"}
              </p>

              {/* Rating Emojis */}
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
              }}>
                {ratingOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => setFeedbackRating(option.value)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      cursor: "pointer",
                      padding: "8px",
                      borderRadius: "8px",
                      backgroundColor: feedbackRating === option.value ? "#e8f0fe" : "transparent",
                      border: feedbackRating === option.value ? "2px solid #1E4DE3" : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: "32px" }}>{option.emoji}</span>
                    <span style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>{option.label}</span>
                  </div>
                ))}
              </div>

              {/* Comments TextField */}
              <TextField
                multiline
                rows={4}
                placeholder="Tell us more about your experience (optional)"
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value.slice(0, 500))}
                fullWidth
                sx={{
                  marginBottom: "8px",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "14px",
                  },
                }}
              />
              <p style={{ margin: "0 0 16px 0", color: "#999", fontSize: "12px" }}>
                {500 - feedbackComments.length}/500 characters left
              </p>

              {/* Consent Checkbox */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={feedbackConsent}
                    onChange={(e) => setFeedbackConsent(e.target.checked)}
                    sx={{ color: "#1E4DE3", "&.Mui-checked": { color: "#1E4DE3" } }}
                  />
                }
                label={
                  <span style={{ fontSize: "13px", color: "#666" }}>
                    I consent to PubVision AI contacting me based on my feedback
                  </span>
                }
              />

              {/* Buttons */}
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "20px",
              }}>
                <Button
                  variant="outlined"
                  onClick={() => setFeedbackDialogOpen(false)}
                  sx={{
                    textTransform: "none",
                    borderColor: "#ccc",
                    color: "#333",
                    "&:hover": {
                      borderColor: "#999",
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || feedbackRating === 0}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#1E4DE3",
                    "&:hover": {
                      backgroundColor: "#1a3fb8",
                    },
                  }}
                >
                  {submittingFeedback ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Submit feedback"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
PosterViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  posterUrl: PropTypes.string,
  posterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStartRegenerate: PropTypes.func,
  onDownload: PropTypes.func,
};

export default PosterViewDialog;
